import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { ArticleDisclaimer } from "@/components/site/ArticleDisclaimer";
import { ShareBar } from "@/components/site/ShareBar";
import { socialImageMeta, SITE_URL } from "@/lib/social";
import { makeSeoDescription } from "@/lib/seo-description";
import { makeShortTitle } from "@/lib/seo-title";
import { buildArticleLd } from "@/lib/article-schema";
import { AuthorBio } from "@/components/site/AuthorBio";
import { EditorNote } from "@/components/site/EditorNote";
import { RelatedPublicArticles } from "@/components/site/RelatedPublicArticles";
import { SafeImage } from "@/components/site/SafeImage";
import { lazifyHtml } from "@/lib/lazy-html";

interface PublicArticle {
  title: string;
  slug: string;
  country: string;
  category: string;
  excerpt: string;
  hero_image_url: string | null;
  hero_image_alt?: string | null;
  body_html: string;
  published_at: string;
  updated_at: string;
}

const fetchPost = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }): Promise<PublicArticle | null> => {
    const db = supabase as unknown as SupabaseClient;
    const { data: row } = await db
      .from("articles")
      .select(
        "title, slug, country, category, excerpt, hero_image_url, hero_image_alt, body_html, published_at, updated_at",
      )
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    return (row as PublicArticle | null) ?? null;
  });

export const Route = createFileRoute("/post/$slug")({
  loader: async ({ params }) => {
    const post = await fetchPost({ data: { slug: params.slug } });
    if (!post) throw notFound();
    // WebP + lazy loading + responsive sizes for every body image.
    return { ...post, body_html: lazifyHtml(post.body_html) };
  },
  head: ({ params, loaderData }) => {
    const title = loaderData?.title ?? "Article";
    const description = loaderData?.excerpt ?? "Expat guidance from LifeWestWay.";
    const image = loaderData?.hero_image_url ?? undefined;
    const url = `${SITE_URL}/post/${params.slug}`;
    return {
      meta: [
        { title: makeShortTitle(title) },
        { name: "description", content: makeSeoDescription(description.slice(0, 158)) },
        { property: "og:title", content: title },
        { property: "og:description", content: makeSeoDescription(description.slice(0, 158)) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "LifeWestWay" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: makeSeoDescription(description.slice(0, 158)) },
        ...socialImageMeta({ image, alt: loaderData?.hero_image_alt?.trim() || title }),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildArticleLd({
              url,
              headline: title,
              description,
              image,
              datePublished: loaderData?.published_at ?? new Date().toISOString(),
              dateModified: loaderData?.updated_at ?? loaderData?.published_at,
              section: loaderData?.category,
              about: loaderData?.country,
            }),
          ),
        },
      ],
    };
  },
  component: PostPage,
});

function PostPage() {
  const post = Route.useLoaderData();

  return (
    <article className="mx-auto w-full max-w-3xl px-4 py-10">
      <Breadcrumbs
        items={[
          { label: "Home", href: "/" },
          { label: "Latest", href: "/latest" },
          { label: post.title },
        ]}
      />

      <p className="mt-6 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {post.country.toUpperCase()} · {post.category}
      </p>
      <h1 className="mt-2 font-display text-4xl font-bold leading-tight text-foreground">
        {post.title}
      </h1>
      {post.excerpt ? (
        <p className="mt-4 text-lg text-muted-foreground">{post.excerpt}</p>
      ) : null}
      <p className="mt-3 text-xs text-muted-foreground">
        Published{" "}
        {new Date(post.published_at).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}
      </p>

      {post.hero_image_url ? (
        <SafeImage
          src={post.hero_image_url}
          alt={post.hero_image_alt?.trim() || post.title}
          width={1200}
          height={675}
          eager
          className="mt-6 aspect-[16/9] w-full rounded-xl object-cover"
        />
      ) : null}

      <div
        className="article-body mt-8 space-y-4 text-base leading-relaxed text-foreground/90"
        dangerouslySetInnerHTML={{ __html: post.body_html }}
      />

      <EditorNote
        slug={post.slug}
        href={`/post/${post.slug}`}
        category={post.category}
        country={post.country}
        countryLabel={post.country.toUpperCase()}
        className="not-prose"
      />

      <ArticleDisclaimer />

      <AuthorBio slug={post.slug} />
      
      <RelatedPublicArticles 
        category={post.category} 
        country={post.country} 
        currentSlug={post.slug} 
      />

      <div className="mt-8">
        <Link title="Browse the latest LifeWestWay updates" to="/latest" className="text-sm font-medium text-primary hover:underline">
          ← Back to latest updates
        </Link>
      </div>

      <ShareBar />
    </article>
  );
}
